import { Component, EventEmitter, forwardRef, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterService } from '@app/core/services/container/filter.service';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import { UserService } from '@app/core/services/user/user.service';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { User } from '@app/shared/models/user.interface';
import { CustomOverlayDescriptor } from '@app/shared/services/overlay/custom-overlay.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { StateService } from '@components/data/data-table-new/state/state.service';
import {
  InputStringtableComponent,
  StringTableStrategy
} from '@components/form-controls/inputs/input-stringtable/input-stringtable.component';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, startWith, tap } from 'rxjs/operators';
import { UserSelectionWidgetTableData } from './user-selection-widget.component.table';

@Component({
  selector: 'vfs-user-selection-widget',
  templateUrl: './user-selection-widget.component.html',
  styleUrls: ['./user-selection-widget.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserSelectionWidgetComponent)
    }
  ]
})
export class UserSelectionWidgetComponent implements OnInit, ControlValueAccessor {
  tableState$: Observable<DataTableState>;
  globalDivisionFilterTrigger$: Observable<any>;
  all$: Observable<any>;
  stringTableData: User[] = [];
  initialSelection$: Observable<User[]>;
  @Input() selection = new EntitySelectionModel<User>(true, []);
  @Input() ngModel: string[];
  @Output() ngModelChange = new EventEmitter<string[]>();
  @Output() selectedChange = new EventEmitter<User[]>();
  @Output() clearEvt = new EventEmitter();
  @Input() overlaySubject$: Subject<boolean>;
  @Input() customOverlayDescriptor: CustomOverlayDescriptor;
  @Input() closeSidenav = true;
  @Input() label: string;
  @ViewChild(InputStringtableComponent) inputStringtable: InputStringtableComponent;

  // ControlValueAccessor
  disabled: boolean;
  value: any;

  constructor(
    @Inject('StateService') protected stateService: StateService,
    protected divisionContextService: DivisionContextService,
    protected filterService: FilterService,
    protected userService: UserService,
    public userSelectionWidgetTableData: UserSelectionWidgetTableData
  ) {}

  ngOnInit() {
    if (this.ngModel && this.ngModel.length > 0) {
      this.initialSelection$ = this.userService
        .getByIds(this.ngModel)
        .pipe(tap(users => this.selectedChange.emit(users)));
    } else {
      this.initialSelection$ = of([]);
    }

    this.tableState$ = this.userSelectionWidgetTableData.stateSubject$.pipe(
      startWith(this.getState()),
      tap(newState => {
        this.refreshData(newState);
      })
    );
    this.globalDivisionFilterTrigger$ = this.divisionContextService.divisionFilter$
      .asObservable()
      .pipe(tap(_ => this.refreshData(this.getState())));

    this.all$ = combineLatest(this.tableState$, this.globalDivisionFilterTrigger$);
  }

  protected refreshData(state: DataTableState) {
    this.userSelectionWidgetTableData.dataSource.loading = true;
    let filters: string = this.filterService.buildFilterForSearchNew(
      state.filter,
      this.userSelectionWidgetTableData.searchFilters
    );
    filters += this.getMandatoryFilters(filters);

    this.userService
      .search(state.page, state.sort, state.filter.search, filters)
      .pipe(
        catchError(error => throwError(error)),
        tap(page => {
          this.userSelectionWidgetTableData.dataSource.setPageData(page);
          this.userSelectionWidgetTableData.dataSource.loading = false;
        })
      )
      .subscribe();
  }

  protected getMandatoryFilters(filters: string) {
    return (filters.length > 0 ? ' and ' : '') + `(* ${RSQLOperator.EXCLUDE_B2B_USER} *)`;
  }

  protected getState() {
    const exists: boolean = this.stateService.exists(this.userSelectionWidgetTableData.stateId);
    const state: DataTableState = this.stateService.get(this.userSelectionWidgetTableData.stateId);

    if (!exists) {
      if (state.sort.column === '') {
        state.sort.column = 'name';
      }

      state.sort = {
        column: 'name',
        direction: 'asc'
      };
      this.stateService.update(state);
    }

    return state;
  }

  public userStrategy: StringTableStrategy<User> = (selection: EntitySelectionModel<User>) => {
    this.ngModel = selection.selected.map(device => device.id);
    this.ngModelChange.emit(selection.selected.map(device => device.id));
    this.selectedChange.emit(selection.selected);
    return selection.selected.map(device => device.name);
  };

  masterToggle() {
    this.checkIfDataIsSelected(this.selection, this.userSelectionWidgetTableData.dataSource.data)
      ? this.selection.clear()
      : this.userSelectionWidgetTableData.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkIfDataIsSelected(selection: EntitySelectionModel<User>, data: User[]): boolean {
    return data.every(user => selection.isSelected(user));
  }

  public getDivisions(user: User): string {
    return user.userDivisions.map(userDivision => userDivision.division.description).join(', ');
  }

  clear() {
    this.ngModelChange.emit([]);
    this.selectedChange.emit([]);
    this.clearEvt.emit();
  }

  // ControlValueAccessor functions
  onChange = (rating: number) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
